<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "What accounts exist, what do they hold, and which ones can I post to?"
  Answer:   the statement itself - class sections in accounting order, each account's icon,
            name, code and, where the balance capability allows it, its own currency and its
            balance in that currency, sharing one decimal edge.
  Context:  each parent's sub-account count; the archived and hidden badges on a row.
  Controls: "Add account" is the one action-coloured control in scope. Reconcile is secondary.
            The class chips, search and type Select are neutral controls above the table; each
            row's overflow menu carries the maintenance actions.

  Unlike Transactions (capped at a measured 70rem), a chart of accounts has no such ceiling -
  row count varies with the ledger, so the table fills the available width instead.
-->
<script setup lang="ts">
import {
  ActionButton,
  IconButton,
  MenuPopover,
  MenuSwitchItem,
  PageHeader,
  PageShell,
  Select,
  type SelectOption,
} from '@taler/ui'
import { formatLocalDate, systemClock, todayUtc } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  AccountArchiveDialog,
  AccountClassChips,
  AccountCreateDialog,
  ACCOUNT_TYPES,
  AccountSearchBar,
  AccountStatementTable,
  canArchiveAccount,
  canCreateAccount,
  canEditAccount,
  canHideAccount,
  canViewAccountBalances,
  canViewAccounts,
  homeCurrencyOf,
  useNativeAccountBalances,
  useAccountMaintenance,
  ALL_TYPES,
  useAccountsTree,
} from '@/features/accounts'
import { canCreateEntries, useDefaultPaymentAccount } from '@/features/entries'
import { canReconcile as canReconcileCapability, useOpenReconciliation } from '@/features/reconciliation'
import { useSession } from '@/app/session'
import { useToasts } from '@/app/toasts'

const { t, locale } = useI18n()
const session = useSession()
const router = useRouter()

const tableLocale = computed(() => locale.value as 'en' | 'hu')

const canView = computed(() => canViewAccounts(session.can))
const canViewBalances = computed(() => canViewAccountBalances(session.can))
const canCreate = computed(() => canCreateAccount(session.can))
const canEdit = computed(() => canEditAccount(session.can))
const canHide = computed(() => canHideAccount(session.can))
const canArchive = computed(() => canArchiveAccount(session.can))
const canReconcile = computed(() => canReconcileCapability(session.can))
const canSetDefault = computed(() => canCreateEntries(session.can))
const defaultAccountId = computed(() => session.activeLedger.value?.defaultPaymentAccountId)
const defaultPaymentAccount = useDefaultPaymentAccount({ ledgerId: () => session.activeLedger.value?.id })

const openReconciliation = useOpenReconciliation({
  ledgerId: () => session.activeLedger.value?.id,
  enabled: () => canReconcile.value,
})

const { push: pushToast } = useToasts()
async function reconcileAccounts(): Promise<void> {
  let id: string
  try {
    id = openReconciliation.summary.value?.id ?? (await openReconciliation.start(todayUtc(systemClock)))
  } catch {
    pushToast('danger', t('accounts.reconcileFailed'))
    return
  }
  await router.push({ name: 'reconciliation', params: { id } })
}

const tree = useAccountsTree({
  ledgerId: () => session.activeLedger.value?.id,
  locale: () => tableLocale.value,
  enabled: () => canView.value,
})

const today = () => formatLocalDate(todayUtc(systemClock))
const nativeBalances = useNativeAccountBalances({
  ledgerId: () => session.activeLedger.value?.id,
  accounts: () =>
    tree.rows.value.flatMap((row) => {
      const currency = homeCurrencyOf(row)
      return currency === undefined ? [] : [{ id: row.id, accountClass: row.accountClass, currency }]
    }),
  until: today,
  enabled: () => canViewBalances.value && session.activeLedger.value !== undefined,
})

const maintenance = useAccountMaintenance(tree)

type TypeFilter = (typeof ACCOUNT_TYPES)[number] | typeof ALL_TYPES
const typeOptions = computed<SelectOption<TypeFilter>[]>(() => [
  { value: ALL_TYPES, label: t('accounts.allTypes') },
  ...ACCOUNT_TYPES.map((value) => ({ value, label: t(`accounts.types.${value}`) })),
])
</script>

<template>
  <PageShell class="gap-[var(--space-section)]">
    <PageHeader :title="t('accounts.title')">
      <template #subtitle>{{ t('accounts.subtitle') }}</template>
      <template #actions>
        <AccountSearchBar
          v-if="canView && session.activeLedger.value !== undefined"
          :search="tree.search.value"
          @update:search="tree.setSearch($event)"
        />
        <ActionButton
          v-if="canReconcile"
          variant="secondary"
          type="button"
          :disabled="openReconciliation.isStarting.value"
          @click="reconcileAccounts"
        >
          {{
            openReconciliation.summary.value !== undefined
              ? t('accounts.resumeReconciliation')
              : t('accounts.reconcileAccounts')
          }}
        </ActionButton>
        <ActionButton size="lg" v-if="canCreate" type="button" @click="maintenance.openCreate()">
          {{ t('accounts.addAccount') }}
        </ActionButton>
      </template>
    </PageHeader>

    <p
      v-if="canReconcile && openReconciliation.summary.value !== undefined"
      class="rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-attention)_10%,transparent)] px-[var(--space-cell)] py-[var(--space-inline)] text-[color:var(--color-text)]"
    >
      {{
        t('accounts.reconciliationOpenBanner', { count: openReconciliation.summary.value.accountsChecked })
      }}
      <RouterLink
        :to="{ name: 'reconciliation', params: { id: openReconciliation.summary.value.id } }"
        class="underline"
      >
        {{ t('accounts.resumeReconciliation') }}
      </RouterLink>
    </p>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('accounts.noLedger') }}
    </p>

    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">
      {{ t('accounts.noAccess') }}
    </p>

    <div v-else class="flex flex-1 flex-col gap-[var(--space-group)]">
      <div class="flex flex-wrap items-center justify-between gap-[var(--space-group)]">
        <AccountClassChips
          :summaries="tree.classSummaries.value"
          :class-filter="tree.classFilter.value"
          @update:class-filter="tree.setClassFilter($event)"
        />
        <div class="flex items-center gap-[var(--space-inline)]">
          <Select
            :model-value="tree.typeFilter.value"
            :options="typeOptions"
            :aria-label="t('accounts.type')"
            variant="field"
            @update:model-value="tree.setTypeFilter($event)"
          />
          <MenuPopover side="bottom" align="end">
            <template #trigger>
              <IconButton variant="bordered" :aria-label="t('accounts.moreActions')">
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
                  <circle cx="3.5" cy="8" r="1.4" />
                  <circle cx="8" cy="8" r="1.4" />
                  <circle cx="12.5" cy="8" r="1.4" />
                </svg>
              </IconButton>
            </template>
            <MenuSwitchItem
              :model-value="tree.showArchived.value"
              :label="t('accounts.showArchived')"
              description=""
              @update:model-value="tree.toggleShowArchived()"
            />
            <MenuSwitchItem
              :model-value="tree.showHidden.value"
              :label="t('accounts.showHidden')"
              description=""
              @update:model-value="tree.toggleShowHidden()"
            />
          </MenuPopover>
        </div>
      </div>

      <div class="flex min-h-[32rem] flex-1 items-stretch gap-[var(--space-block)]">
        <div class="min-w-0 flex-1 overflow-y-auto">
          <AccountStatementTable
            :rows="tree.rows.value"
            :balances="nativeBalances.byAccount.value"
            :locale="tableLocale"
            :can-view-balances="canViewBalances"
            :can-create="canCreate"
            :can-edit="canEdit"
            :can-hide="canHide"
            :can-archive="canArchive"
            :can-reconcile="canReconcile"
            :can-set-default="canSetDefault"
            :is-busy="tree.isCreating.value || defaultPaymentAccount.isSetting.value"
            :default-account-id="defaultAccountId"
            :is-loading="tree.isLoading.value || (canViewBalances && nativeBalances.isLoading.value)"
            :error="tree.error.value ?? nativeBalances.error.value"
            :retrying="tree.retrying.value"
            @retry="tree.retry()"
            @add-child="maintenance.openCreate($event)"
            @edit="maintenance.openEdit($event)"
            @hide="tree.hide($event)"
            @unhide="tree.unhide($event)"
            @restore="maintenance.restore($event)"
            @archive-request="maintenance.requestArchive($event)"
            @reconcile="reconcileAccounts"
            @set-default="defaultPaymentAccount.set($event)"
          />
        </div>
      </div>
    </div>

    <AccountArchiveDialog
      :open="maintenance.archiveRow.value !== undefined"
      :label="maintenance.archiveRow.value?.label ?? ''"
      :is-submitting="maintenance.isArchiving.value"
      :has-error="maintenance.archiveHasError.value"
      @confirm="maintenance.submitArchive()"
      @close="maintenance.closeArchive()"
    />

    <AccountCreateDialog
      :open="maintenance.createOpen.value"
      :default-parent-id="maintenance.createParentId.value"
      :parent-options="tree.parentOptions.value"
      :is-submitting="tree.isCreating.value"
      :error="maintenance.createError.value"
      @submit="maintenance.submitCreate"
      @close="maintenance.closeCreate()"
    />
  </PageShell>
</template>
